import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { ReviewAnnotation, ReviewAnnotationType, ReviewCategory, Scene, SceneReviewStatus } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useTts } from "../workspace/useTts.js";
import { chunkForTtsSegments, ttsChunkMax } from "../lib/tts.js";
import { anchorFromSelection, buildReadModel, rangeFromOffsets, resolveAnchor, type BuiltAnchor } from "./anchor.js";
import { ReviewPanel } from "./ReviewPanel.js";
import { ComposerModal } from "./Composer.js";
import { AskScribe } from "./AskScribe.js";
import { spliceRewrite } from "./apply.js";
import { ALL_KINDS, paintAnnotations, setHighlights } from "./highlights.js";
import { STATUS_LABELS, STATUS_ORDER } from "./constants.js";

interface Props {
  projectId: string;
  scene: Scene;
  chapterTitle: string;
  reviewStatus: SceneReviewStatus;
  onStatusChange: (status: SceneReviewStatus) => void;
  onExit: () => void;
  onApplied: (scene: Scene) => void;
  view: "scroll" | "book";
  onToggleView: () => void;
}

export function ReviewScene({ projectId, scene, chapterTitle, reviewStatus, onStatusChange, onExit, onApplied, view, onToggleView }: Props) {
  const toast = useToast();
  const proseRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [annotations, setAnnotations] = useState<ReviewAnnotation[]>([]);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [toolbar, setToolbar] = useState<{ x: number; y: number; built: BuiltAnchor } | null>(null);
  const [composer, setComposer] = useState<{ type: ReviewAnnotationType; built: BuiltAnchor | null } | null>(null);
  const [ask, setAsk] = useState<{ built: BuiltAnchor } | null>(null);
  const [applying, setApplying] = useState(false);
  const [voice, setVoice] = useState<string>("");
  const tts = useTts(projectId);

  const load = useCallback(async () => {
    try {
      const { annotations: list } = await api.reviewAnnotations(projectId, { scope: "scene", sceneId: scene.id });
      setAnnotations(list);
    } catch { /* ignore */ }
  }, [projectId, scene.id]);

  useEffect(() => { void load(); }, [load]);

  // Clear all review highlights when the scene changes or the view unmounts.
  useEffect(() => () => { for (const k of ALL_KINDS) setHighlights(k, []); }, [scene.id]);

  // ---- Selection → contextual toolbar ----
  const captureSelection = useCallback(() => {
    const root = proseRef.current;
    const sel = window.getSelection();
    if (!root || !sel || sel.isCollapsed || sel.rangeCount === 0) { setToolbar(null); return; }
    const range = sel.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) { setToolbar(null); return; }
    const built = anchorFromSelection(root, sel);
    if (!built) { setToolbar(null); return; }
    const rect = range.getBoundingClientRect();
    const host = scrollRef.current?.getBoundingClientRect();
    if (!host) return;
    setToolbar({
      x: rect.left - host.left + rect.width / 2,
      y: rect.top - host.top - 8,
      built
    });
  }, []);

  useEffect(() => {
    const onUp = () => window.setTimeout(captureSelection, 0);
    document.addEventListener("mouseup", onUp);
    document.addEventListener("keyup", onUp);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("keyup", onUp);
    };
  }, [captureSelection]);

  const dismissToolbar = useCallback(() => {
    setToolbar(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  // ---- Create annotations ----
  const createQuick = useCallback(async (type: "highlight" | "strike") => {
    if (!toolbar) return;
    try {
      const { annotation } = await api.createReviewAnnotation(projectId, {
        chapterId: scene.chapterId,
        sceneId: scene.id,
        type,
        anchorType: "text",
        anchor: toolbar.built.anchor,
        selectedText: toolbar.built.selectedText
      });
      setAnnotations((prev) => [...prev, annotation]);
      dismissToolbar();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not add markup");
    }
  }, [toolbar, projectId, scene.chapterId, scene.id, dismissToolbar, toast]);

  const openComposer = useCallback((type: ReviewAnnotationType) => {
    setComposer({ type, built: toolbar?.built ?? null });
    setToolbar(null);
  }, [toolbar]);

  const startAsk = useCallback(() => {
    if (!toolbar) return;
    setAsk({ built: toolbar.built });
    setToolbar(null);
  }, [toolbar]);

  const acceptAsk = useCallback(async (after: string) => {
    if (!ask || !proseRef.current) return;
    const newHtml = spliceRewrite(proseRef.current, ask.built.anchor, ask.built.selectedText, after);
    if (newHtml === null) { toast.error("Couldn't locate that passage to replace — it may have changed."); return; }
    setApplying(true);
    try {
      const { scene: updated } = await api.applyReviewRewrite(projectId, scene.id, newHtml, "Review rewrite");
      onApplied(updated);
      setAsk(null);
      await load();
      toast.success("Rewrite applied — saved to version history.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not apply the rewrite.");
    } finally {
      setApplying(false);
    }
  }, [ask, projectId, scene.id, onApplied, load, toast]);

  const saveComposer = useCallback(async (note: string, category: ReviewCategory | null) => {
    if (!composer) return;
    try {
      const { annotation } = await api.createReviewAnnotation(projectId, {
        chapterId: scene.chapterId,
        sceneId: scene.id,
        type: composer.type,
        category,
        anchorType: composer.built ? "text" : "scene",
        anchor: composer.built?.anchor ?? null,
        selectedText: composer.built?.selectedText ?? null,
        note: note.trim() || null
      });
      setAnnotations((prev) => [...prev, annotation]);
      setComposer(null);
      window.getSelection()?.removeAllRanges();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save note");
    }
  }, [composer, projectId, scene.chapterId, scene.id, toast]);

  const updateAnnotation = useCallback(async (id: string, patch: Parameters<typeof api.updateReviewAnnotation>[2]) => {
    try {
      const { annotation } = await api.updateReviewAnnotation(projectId, id, patch);
      setAnnotations((prev) => prev.map((a) => (a.id === id ? annotation : a)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Update failed");
    }
  }, [projectId, toast]);

  const deleteAnnotation = useCallback(async (id: string) => {
    try {
      await api.deleteReviewAnnotation(projectId, id);
      setAnnotations((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  }, [projectId, toast]);

  // ---- Render annotation highlights (non-destructive, CSS Highlight API) ----
  useEffect(() => {
    const root = scrollRef.current;
    if (!root) return;
    paintAnnotations(root, annotations, { show: showAnnotations, activeId }, (id, orphaned) =>
      void updateAnnotation(id, { orphaned })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations, showAnnotations, activeId, scene.content]);

  // ---- Jump to an annotation (scroll + flash active) ----
  const jumpTo = useCallback((ann: ReviewAnnotation) => {
    setActiveId(ann.id);
    const root = proseRef.current;
    const scroller = scrollRef.current;
    if (!root || !scroller || ann.anchorType !== "text") return;
    const model = buildReadModel(root);
    const resolved = resolveAnchor(model, ann.anchor, ann.selectedText);
    if (!resolved.range) { toast.info("That passage can't be found — the text may have changed."); return; }
    const rect = resolved.range.getBoundingClientRect();
    const box = scroller.getBoundingClientRect();
    if (rect.height > 0) scroller.scrollBy({ top: rect.top - (box.top + box.height * 0.35), behavior: "smooth" });
  }, [toast]);

  const sortedForNav = useMemo(
    () => annotations.filter((a) => a.anchorType === "text").slice(),
    [annotations]
  );
  const stepAnnotation = useCallback((dir: 1 | -1) => {
    if (sortedForNav.length === 0) return;
    const idx = sortedForNav.findIndex((a) => a.id === activeId);
    const next = sortedForNav[(idx + dir + sortedForNav.length) % sortedForNav.length];
    if (next) jumpTo(next);
  }, [sortedForNav, activeId, jumpTo]);

  // ---- TTS read-along highlight for the current scene ----
  useEffect(() => {
    const root = proseRef.current;
    if (!root) return;
    if (tts.state === "idle" || tts.nowPlaying < 0) { setHighlights("review-reading", []); return; }
    const model = buildReadModel(root);
    const segs = chunkForTtsSegments(model.text, ttsChunkMax(!!tts.status?.available));
    const seg = segs[tts.nowPlaying];
    if (!seg) { setHighlights("review-reading", []); return; }
    const range = rangeFromOffsets(model, seg.start, seg.end);
    setHighlights("review-reading", range ? [range] : []);
    if (range && scrollRef.current) {
      const rect = range.getBoundingClientRect();
      const box = scrollRef.current.getBoundingClientRect();
      if (rect.height > 0 && (rect.top < box.top + box.height * 0.2 || rect.bottom > box.bottom - box.height * 0.2)) {
        scrollRef.current.scrollBy({ top: rect.top - (box.top + box.height * 0.35), behavior: "smooth" });
      }
    }
  }, [tts.nowPlaying, tts.state, tts.status?.available]);

  const readScene = useCallback(() => {
    const text = proseRef.current?.innerText?.trim();
    if (!text) { toast.info("Nothing to read in this scene yet."); return; }
    void tts.play(text, voice || undefined).catch((err) => toast.error(err instanceof Error ? err.message : "Narration failed."));
  }, [tts, voice, toast]);

  // Alt+click to read from that point forward.
  const onProseClick = useCallback((e: React.MouseEvent) => {
    if (!e.altKey) return;
    const root = proseRef.current;
    if (!root) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const model = buildReadModel(root);
    // Map the caret to an offset by selecting from start of root to the caret.
    const caret = sel.getRangeAt(0);
    const probe = document.createRange();
    probe.selectNodeContents(root);
    probe.setEnd(caret.startContainer, caret.startOffset);
    const offset = probe.toString().length;
    const from = model.text.slice(Math.max(0, offset));
    if (from.trim()) void tts.play(from).catch(() => undefined);
  }, [tts]);

  // ---- Keyboard shortcuts (scoped to Review Mode) ----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (composer) { if (e.key === "Escape") setComposer(null); return; }
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (e.key === "Escape") { if (toolbar) dismissToolbar(); return; }
      if (e.key === " " && (tts.state === "playing" || tts.state === "paused")) {
        e.preventDefault();
        tts.state === "playing" ? tts.pause() : tts.resume();
        return;
      }
      if (!toolbar) {
        if (e.key === "[") { e.preventDefault(); stepAnnotation(-1); }
        if (e.key === "]") { e.preventDefault(); stepAnnotation(1); }
        return;
      }
      const k = e.key.toLowerCase();
      if (k === "h") { e.preventDefault(); void createQuick("highlight"); }
      else if (k === "s") { e.preventDefault(); void createQuick("strike"); }
      else if (k === "c") { e.preventDefault(); openComposer("comment"); }
      else if (k === "f") { e.preventDefault(); openComposer("flag"); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toolbar, composer, tts, createQuick, openComposer, dismissToolbar, stepAnnotation]);

  const unresolvedCount = annotations.filter((a) => a.status === "unresolved").length;

  return (
    <div className="review-mode">
      <header className="review-topbar">
        <div className="row gap-2" style={{ minWidth: 0 }}>
          <button className="btn btn-sm btn-ghost" onClick={onExit} title="Exit Review Mode">
            <Icon name="x" size={15} /> Exit Review
          </button>
          <span className="review-eyebrow truncate">{chapterTitle} · Review</span>
        </div>
        <div className="spacer" />
        {/* TTS */}
        <div className="review-tts">
          {tts.status && tts.status.voices.length > 1 && tts.state === "idle" && (
            <select className="select select-sm" title="Narrator voice"
              value={voice} onChange={(e) => setVoice(e.target.value)}>
              <option value="">{tts.status.defaultVoice || "Default"}</option>
              {tts.status.voices.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
          {tts.state === "idle" ? (
            <button className="btn btn-sm btn-ghost" onClick={readScene} title="Read this scene aloud">
              <Icon name="volume" size={15} /> Read aloud
            </button>
          ) : (
            <span className="row gap-1">
              <span className="faint" style={{ fontSize: 11 }}>{tts.progress.index}/{tts.progress.total}</span>
              {tts.state === "playing"
                ? <button className="btn btn-icon btn-ghost btn-sm" title="Pause" onClick={tts.pause}><Icon name="pause" size={15} /></button>
                : <button className="btn btn-icon btn-ghost btn-sm" title="Resume" onClick={tts.resume}><Icon name="play" size={15} /></button>}
              <button className="btn btn-icon btn-ghost btn-sm" title="Stop" onClick={tts.stop}><Icon name="stop" size={14} /></button>
            </span>
          )}
        </div>
        <span className="review-sep" />
        <button className={`btn btn-sm ${view === "book" ? "btn-primary" : "btn-ghost"}`} onClick={onToggleView}
          title="Book / Page View">
          <Icon name="book" size={15} /> Book View
        </button>
        <select className="select select-sm" value={reviewStatus}
          onChange={(e) => onStatusChange(e.target.value as SceneReviewStatus)} title="Scene review status">
          {STATUS_ORDER.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <button className={`btn btn-icon btn-sm ${showAnnotations ? "btn-ghost" : "tool-btn-active"}`}
          title={showAnnotations ? "Hide annotations" : "Show annotations"}
          onClick={() => setShowAnnotations((v) => !v)}>
          <Icon name={showAnnotations ? "edit" : "file"} size={15} />
        </button>
        <button className="btn btn-icon btn-sm btn-ghost" title="Previous annotation ([)" onClick={() => stepAnnotation(-1)}>
          <Icon name="chevronUp" size={15} />
        </button>
        <button className="btn btn-icon btn-sm btn-ghost" title="Next annotation (])" onClick={() => stepAnnotation(1)}>
          <Icon name="chevronDown" size={15} />
        </button>
        <button className={`btn btn-sm ${panelOpen ? "btn-primary" : "btn-ghost"}`} onClick={() => setPanelOpen((v) => !v)}>
          <Icon name="note" size={15} /> Review{unresolvedCount > 0 ? ` (${unresolvedCount})` : ""}
        </button>
      </header>

      <div className="review-body">
        <div className="review-scroll" ref={scrollRef} onClick={onProseClick}>
          <article className="review-prose reader-prose">
            <h2 className="review-scene-title">{scene.title}</h2>
            <div ref={proseRef} data-scene-id={scene.id} dangerouslySetInnerHTML={{ __html: scene.content || "<p><em>This scene is empty.</em></p>" }} />
          </article>

          {toolbar && (
            <div className="review-toolbar" style={{ left: toolbar.x, top: toolbar.y }} onMouseDown={(e) => e.preventDefault()}>
              <button title="Highlight (H)" onClick={() => void createQuick("highlight")}><Icon name="edit" size={14} /></button>
              <button title="Comment (C)" onClick={() => openComposer("comment")}><Icon name="note" size={14} /></button>
              <button title="Strike (S)" onClick={() => void createQuick("strike")}><Icon name="minus" size={14} /></button>
              <button title="Flag (F)" onClick={() => openComposer("flag")}><Icon name="tag" size={14} /></button>
              <span className="review-toolbar-sep" />
              <button title="Ask Scribe" onClick={startAsk}><Icon name="sparkles" size={14} /></button>
            </div>
          )}
        </div>

        {panelOpen && (
          <ReviewPanel
            annotations={annotations}
            activeId={activeId}
            onJump={jumpTo}
            onResolve={(id, resolved) => void updateAnnotation(id, { status: resolved ? "resolved" : "unresolved" })}
            onDelete={deleteAnnotation}
            onEditNote={(id, note) => void updateAnnotation(id, { note })}
            onAddSceneNote={() => setComposer({ type: "scene_note", built: null })}
            onClose={() => setPanelOpen(false)}
          />
        )}
      </div>

      {composer && (
        <ComposerModal
          type={composer.type}
          selectedText={composer.built?.selectedText ?? null}
          onCancel={() => setComposer(null)}
          onSave={saveComposer}
        />
      )}

      {ask && (
        <AskScribe
          projectId={projectId}
          sceneId={scene.id}
          selectedText={ask.built.selectedText}
          applying={applying}
          onCancel={() => { if (!applying) setAsk(null); }}
          onAccept={(after) => void acceptAsk(after)}
        />
      )}
    </div>
  );
}
